import React, { useEffect, useState } from 'react';

import { ProgressBarStepType } from '../../features/RestrictNavigation/ContentRequirements';
import { useContentRequirementsContext } from '../../features/RestrictNavigation/ContentRequirementsProvider';
import { usePrevious } from '../../hooks/usePrevious';
import { Guid } from '../../store/model/guid';
import { AudioProgressStep } from './AudioProgressStep';
import { BranchingProgressStep } from './BranchingProgressStep';
import { CompletedProgressStep } from './CompletedProgressStep';
import { InProgressStep } from './InProgressStep';
import { ProgressBarStepModel } from './model';
import { ProgressStepComponent } from './ProgressStepComponent';
import { RestrictedProgressStep } from './RestrictedProgressStep';

export interface ProgressBarStepProps extends ProgressBarStepModel {
  onClick?: () => void;
}

// We want to blink in case item requirements were just fulfilled, but not in case we switch to completed item
// or item had no requirements at all.
const useWasItemJustCompleted = (progressBarType: ProgressBarStepType, itemId: Guid) => {
  const [justCompleted, setJustCompleted] = useState(false);
  const prevProgressBarType = usePrevious(progressBarType);
  const prevItemId = usePrevious(itemId);
  useEffect(() => {
    if (
      itemId === prevItemId &&
      progressBarType === ProgressBarStepType.Completed &&
      prevProgressBarType !== ProgressBarStepType.Completed
    ) {
      setJustCompleted(true);
    }
  }, [itemId, prevProgressBarType, prevItemId, progressBarType]);

  useEffect(() => {
    if (itemId !== prevItemId) {
      setJustCompleted(false);
    }
  }, [itemId, prevItemId]);

  return {
    justCompleted,
  };
};

export const ProgressBarStep: React.FC<ProgressBarStepProps> = (props: ProgressBarStepProps) => {
  const { itemId, progressBarType } = useContentRequirementsContext();

  const { justCompleted } = useWasItemJustCompleted(progressBarType, itemId);

  const { selected } = props;

  if (props.isSplit) {
    return <BranchingProgressStep {...props} />;
  }

  if (!selected) {
    return <ProgressStepComponent {...props} />;
  }

  switch (progressBarType) {
    case ProgressBarStepType.InProgress:
      return <InProgressStep {...props} />;
    case ProgressBarStepType.Audio:
      return <AudioProgressStep {...props} />;
    case ProgressBarStepType.Completed:
      return <CompletedProgressStep justCompleted={justCompleted} {...props} />;
    case ProgressBarStepType.Restricted:
      return <RestrictedProgressStep {...props} />;
  }
};
